import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { NavSection } from './types'

interface LeftNavStoreState {
  // Navigation state
  isCollapsed: boolean
  activeSection: NavSection | null
  isSlideOutOpen: boolean

  // Actions
  setCollapsed: (collapsed: boolean) => void
  toggleCollapsed: () => void
  setActiveSection: (section: NavSection | null) => void
  setSlideOutOpen: (open: boolean) => void
  toggleSlideOut: () => void
  openSlideOut: (section: NavSection) => void
  closeSlideOut: () => void

  // Last visited paths for each section (for breadcrumb navigation)
  lastVisited: Record<NavSection, string>
  /** Updates the last visited path for a specific navigation section */
  setLastVisited: (section: NavSection, path: string) => void
  /** Resets all last visited paths to defaults. Called on logout to prevent cross-user state leakage (FIR-5982) */
  resetLastVisited: () => void
  /** Syncs default paths from resolved nav items. Entries still matching the old hardcoded default are updated to the new resolved value. */
  syncDefaultPaths: (
    resolvedDefaults: Partial<Record<NavSection, string>>
  ) => void
}

/**
 * Default landing pages for each navigation section (FIR-4955)
 *
 * These paths are used when:
 * 1. User clicks a nav section for the first time (no lastVisited stored)
 * 2. User clears their browser data (localStorage reset)
 *
 * Path Selection Rationale:
 * - targets: Commercial > Restaurants & Bars (most common use case)
 * - campaigns: All Campaigns view (overview)
 * - samples/rebates/deals: Root pages (single-level sections)
 * - performance: Dashboard (overview analytics)
 * - workspace: Documents (primary workspace tab)
 * - settings: Users (most frequently accessed settings)
 *
 * Note: These defaults are persisted to localStorage and overwritten as users navigate.
 */
const DEFAULT_PATHS: Record<NavSection, string> = {
  home: '/home',
  targets: '/targets/commercial/restaurants-bars',
  campaigns: '/campaigns',
  samples: '/samples',
  rebates: '/rebates',
  deals: '/deals',
  performance: '/dashboard',
  workspace: '/documents',
  settings: '/settings/users',
}

export const useLeftNavStore = create<LeftNavStoreState>()(
  persist(
    (set) => ({
      // Initial state
      isCollapsed: false,
      activeSection: null,
      isSlideOutOpen: false,
      lastVisited: DEFAULT_PATHS,

      // Actions
      setCollapsed: (collapsed) => set({ isCollapsed: collapsed }),
      toggleCollapsed: () =>
        set((state) => ({ isCollapsed: !state.isCollapsed })),

      setActiveSection: (section) =>
        set((state) => ({
          activeSection: section,
          // Auto-open slide-out when selecting a section that has one
          isSlideOutOpen: section !== null && state.isSlideOutOpen,
        })),

      setSlideOutOpen: (open) => set({ isSlideOutOpen: open }),
      toggleSlideOut: () =>
        set((state) => ({ isSlideOutOpen: !state.isSlideOutOpen })),

      openSlideOut: (section) =>
        set({
          activeSection: section,
          isSlideOutOpen: true,
        }),

      closeSlideOut: () =>
        set({
          isSlideOutOpen: false,
        }),

      setLastVisited: (section, path) =>
        set((state) => ({
          lastVisited: {
            ...state.lastVisited,
            [section]: path,
          },
        })),

      resetLastVisited: () =>
        set({
          lastVisited: DEFAULT_PATHS,
        }),

      syncDefaultPaths: (resolvedDefaults) =>
        set((state) => {
          const updated = { ...state.lastVisited }
          for (const [section, resolvedPath] of Object.entries(
            resolvedDefaults
          )) {
            const key = section as NavSection
            const hardcodedDefault = DEFAULT_PATHS[key]
            // Only overwrite if the stored value still matches the hardcoded default
            if (
              updated[key] === hardcodedDefault &&
              resolvedPath !== hardcodedDefault
            ) {
              updated[key] = resolvedPath
            }
          }
          return { lastVisited: updated }
        }),
    }),
    {
      name: 'left-nav-storage',
      partialize: (state) => ({
        isCollapsed: state.isCollapsed,
        lastVisited: state.lastVisited,
      }),
    }
  )
)
